import axios from 'axios';

// Configuración base - reutilizar configuración de api.js
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 600000
});

// Interceptor para token de autenticación
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Servicio de API para el módulo de Análisis Financiero
 * Consume los endpoints implementados en backend/endpoints/dashboard.py
 */
class FinancialService {
  /**
   * Cargar/generar datos financieros
   * POST /dashboard/financial/load-data-public
   */
  async loadFinancialData(marketplace, forceRefresh = false) {
    const formData = new FormData();
    formData.append('marketplace', marketplace);
    formData.append('force_refresh', forceRefresh.toString());
    
    const response = await api.post('/dashboard/financial/load-data-public', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  }

  /**
   * Obtener dashboard principal con métricas
   * GET /dashboard/financial/dashboard-public
   */
  async getDashboard(marketplace, cacheKey, horizonteTemporal = 'Mensual', tipoVolumen = 'Predicciones') {
    const response = await api.get('/dashboard/financial/dashboard-public', {
      params: {
        marketplace,
        cache_key: cacheKey,
        horizonte_temporal: horizonteTemporal,
        tipo_volumen: tipoVolumen
      }
    });
    
    return response.data;
  }

  /**
   * Análisis por dimensión (proveedor, sku, identificador)
   * GET /dashboard/financial/dimension-analysis-public
   */
  async getDimensionAnalysis(marketplace, cacheKey, dimension = 'proveedor', limit = 10, horizonteTemporal = 'Mensual', tipoVolumen = 'Predicciones') {
    const response = await api.get('/dashboard/financial/dimension-analysis-public', {
      params: {
        marketplace,
        cache_key: cacheKey,
        dimension,
        limit,
        horizonte_temporal: horizonteTemporal,
        tipo_volumen: tipoVolumen
      }
    });
    
    return response.data;
  }

  /**
   * Top utilidad charts
   * GET /dashboard/financial/top-utility-public
   */
  async getTopUtilityCharts(marketplace, cacheKey, dimension = 'proveedor', horizonteTemporal = 'Mensual', tipoVolumen = 'Predicciones') {
    const response = await api.get('/dashboard/financial/top-utility-public', {
      params: {
        marketplace,
        cache_key: cacheKey,
        dimension,
        horizonte_temporal: horizonteTemporal,
        tipo_volumen: tipoVolumen
      }
    });
    
    return response.data;
  }

  /**
   * Distribución y KDE
   * GET /dashboard/financial/distribution-public
   */
  async getDistributionCharts(marketplace, cacheKey, nbins = 30, horizonteTemporal = 'Mensual', tipoVolumen = 'Predicciones') {
    const response = await api.get('/dashboard/financial/distribution-public', {
      params: {
        marketplace,
        cache_key: cacheKey,
        nbins,
        horizonte_temporal: horizonteTemporal,
        tipo_volumen: tipoVolumen
      }
    });
    
    return response.data;
  }

  /**
   * Gráficos comparativos
   * GET /dashboard/financial/comparative-public
   */
  async getComparativeCharts(marketplace, cacheKey, horizonteTemporal = 'Mensual', tipoVolumen = 'Predicciones') {
    const response = await api.get('/dashboard/financial/comparative-public', {
      params: {
        marketplace,
        cache_key: cacheKey,
        horizonte_temporal: horizonteTemporal,
        tipo_volumen: tipoVolumen
      }
    });
    
    return response.data;
  }

  /**
   * Datos detallados con filtros
   * GET /dashboard/financial/detailed-data-public
   */
  async getDetailedData(marketplace, cacheKey, filters = {}, horizonteTemporal = 'Mensual', tipoVolumen = 'Predicciones') {
    const params = {
      marketplace,
      cache_key: cacheKey,
      limit: filters.limit || 100,
      offset: filters.offset || 0,
      horizonte_temporal: horizonteTemporal,
      tipo_volumen: tipoVolumen
    };
    
    if (filters.proveedor_filter) {
      params.proveedor_filter = filters.proveedor_filter;
    }
    if (filters.min_utility !== undefined && filters.min_utility !== null && filters.min_utility !== '') {
      params.min_utility = filters.min_utility;
    }
    if (filters.max_utility !== undefined && filters.max_utility !== null && filters.max_utility !== '') {
      params.max_utility = filters.max_utility;
    }
    
    const response = await api.get('/dashboard/financial/detailed-data-public', { params });
    
    return response.data;
  }

  /**
   * Guardar en base de datos
   * POST /dashboard/financial/save-to-db-public
   */
  async saveToDatabase(marketplace, cacheKey) {
    const formData = new FormData();
    formData.append('marketplace', marketplace);
    formData.append('cache_key', cacheKey);
    
    const response = await api.post('/dashboard/financial/save-to-db-public', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  }

  /**
   * Limpiar caché
   * DELETE /dashboard/financial/clear-cache-public
   */
  async clearCache(marketplace) {
    const response = await api.delete('/dashboard/financial/clear-cache-public', {
      params: { marketplace }
    });
    
    return response.data;
  }

  /**
   * Obtener URL para exportar CSV
   * GET /dashboard/financial/export-csv-public
   */
  getExportCsvUrl(marketplace, cacheKey, filters = {}) {
    const params = new URLSearchParams({
      marketplace,
      cache_key: cacheKey
    });
    
    if (filters.proveedor_filter) {
      params.append('proveedor_filter', filters.proveedor_filter);
    }
    if (filters.min_utility !== undefined && filters.min_utility !== null && filters.min_utility !== '') {
      params.append('min_utility', filters.min_utility.toString());
    }
    if (filters.max_utility !== undefined && filters.max_utility !== null && filters.max_utility !== '') {
      params.append('max_utility', filters.max_utility.toString());
    }
    
    return `${API_BASE_URL}/dashboard/financial/export-csv-public?${params.toString()}`;
  }

  /**
   * Recalcular con fuente de precio específica
   * POST /dashboard/financial/recalculate-with-price-source-public
   */
  async recalculateWithPriceSource(marketplace, cacheKey, fuentePrecio) {
    const params = new URLSearchParams();
    params.append('marketplace', marketplace);
    params.append('cache_key', cacheKey);
    params.append('fuente_precio', fuentePrecio);
    
    const response = await api.post(
      '/dashboard/financial/recalculate-with-price-source-public', 
      params,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    );
    
    return response.data;
  }

  /**
   * Obtener exploración de precios
   * GET /dashboard/financial/price-exploration-public
   */
  async getPriceExploration(marketplace, cacheKey, dimensionFilter = null, horizonteTemporal = 'Mensual', tipoVolumen = 'Predicciones') {
    const params = new URLSearchParams({
      marketplace,
      cache_key: cacheKey,
      horizonte_temporal: horizonteTemporal,
      tipo_volumen: tipoVolumen
    });
    
    if (dimensionFilter && dimensionFilter.length > 0) {
      dimensionFilter.forEach(filter => {
        params.append('dimension_filter', filter);
      });
    }
    
    const response = await api.get('/dashboard/financial/price-exploration-public', {
      params
    });
    
    return response.data;
  }
}

// Exportar instancia única
export default new FinancialService();

